import "./style.scss";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import ArrowSvg from "@/src/svg/CustomUtils/ArrowSvg";
import ProfileSvg from "@/src/svg/CustomUtils/ProfileSvg";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import { ApplicantAccountingFormulaType, IAccountFormulaComment } from "@/src/types/officeAccounting";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { sortFormulaCommentsNewestFirst } from "@/src/utils/officeAccountingMappers";
import { buildSimpleHistoryText, createApplicantHistory } from "@/src/utils/applicants/createApplicantHistory";

type Props = {
  title?: string;
  value: string;
  currency?: string;
  formulaComments?: IAccountFormulaComment[];
  applicantAccountingId?: number;
  applicantId?: number;
  formulaType?: ApplicantAccountingFormulaType;
  onChange: (value: string) => void;
  onRefresh?: () => void | Promise<void>;
  onClose: () => void;
};

const TEXT_PLACEHOLDER = "—";

const stripCurrency = (raw: string) =>
  raw
    .replace(/USD|EUR|UAH/gi, "")
    .replace(/\s+/g, " ")
    .trim();

const parseInitialFormula = (raw: string) => {
  const cleaned = stripCurrency(raw);
  if (!cleaned) return "";
  return cleaned;
};

const evaluateFormula = (input: string): number | null => {
  const cleaned = input
    .trim()
    .replace(/^=/, "")
    .replace(/\s/g, "")
    .replace(/,/g, ".");

  if (!cleaned) return null;
  if (!/^[\d+\-*/.()]+$/.test(cleaned)) return null;

  try {
    const result = Function(`"use strict"; return (${cleaned})`)();
    return typeof result === "number" && Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
};

const formatSum = (n: number): string => {
  const [intPart, decPart] = n.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces}.${decPart}`;
};

export const formatTuitionFeeDisplay = (raw: string, currency?: string): string => {
  const original = String(raw ?? "").trim();
  if (!original) return "";

  const formula = parseInitialFormula(original);
  const sum = evaluateFormula(formula);
  if (sum != null) {
    const formatted = formatSum(sum);
    return currency ? `${formatted} ${currency}` : formatted;
  }

  const expression = formula.replace(/^=/, "").trim();
  const isFormula = original.trim().startsWith("=") || /[+\-*/()]/.test(expression.replace(/[\d\s.,]/g, ""));
  if (!isFormula) return original;

  return original;
};

const POPUP_EDGE_PADDING_PX = 8;

const toFormulaResultInteger = (value: number): number => Math.round(value);

const CustomCalculatePopUp = ({
  title = "Select an option",
  value,
  currency = "USD",
  formulaComments = [],
  applicantAccountingId,
  applicantId,
  formulaType,
  onChange,
  onRefresh,
  onClose,
}: Props) => {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const initialFormula = parseInitialFormula(value);
  // Input keeps the calculation expression; Sum shows the evaluated result separately.
  const [formula, setFormula] = useState(() => initialFormula);
  const [noteValue, setNoteValue] = useState("");
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openLeft, setOpenLeft] = useState(false);

  const displayedComments = useMemo(() => sortFormulaCommentsNewestFirst(formulaComments), [formulaComments]);
  const sumValue = useMemo(() => evaluateFormula(formula), [formula]);
  const formattedSum = sumValue != null ? formatSum(sumValue) : "";

  const formatFormulaResultAmount = (amount: number | undefined) => {
    if (typeof amount !== "number" || !Number.isFinite(amount)) return "";
    return `${formatSum(amount)} ${currency}`;
  };

  useLayoutEffect(() => {
    const popup = popupRef.current;
    if (!popup) return;

    const updatePlacement = () => {
      const parent = (popup.offsetParent as HTMLElement | null) ?? popup.parentElement;
      if (!parent) return;

      const parentRect = parent.getBoundingClientRect();
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      // Matches `.custom-calculate-popup` side offset (`right/left: -19rem`).
      const sideOffsetPx = 19 * rem;
      const table = popup.closest(".office-accounting-table");
      const bounds = table?.getBoundingClientRect() ?? {
        left: 0,
        right: window.innerWidth,
      };
      const boundaryRight = Math.min(bounds.right, window.innerWidth) - POPUP_EDGE_PADDING_PX;

      // End of table / not enough space on the right → open on the left.
      setOpenLeft(parentRect.right + sideOffsetPx > boundaryRight);
    };

    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    const table = popup.closest(".office-accounting-table");
    table?.addEventListener("scroll", updatePlacement);

    return () => {
      window.removeEventListener("resize", updatePlacement);
      table?.removeEventListener("scroll", updatePlacement);
    };
  }, []);

  const handleClear = () => {
    setFormula("");
    setNoteValue("");
  };

  const handleDone = async () => {
    if (sumValue == null || isSubmitting) return;
    if (!formulaType || applicantAccountingId == null || applicantAccountingId <= 0) return;

    const note = noteValue.trim();
    const nextFormula = formula.trim();
    if (!nextFormula || !note) return;

    setIsSubmitting(true);
    try {
      const result = await officeAccountingPageRequests.createApplicantAccountingFormula({
        applicant_accounting_id: applicantAccountingId,
        type: formulaType,
        formula: nextFormula,
        comment: {
          formula_result_new: toFormulaResultInteger(sumValue),
          note,
        },
      });
      if (!result.ok) return;

      await createApplicantHistory(
        applicantId,
        buildSimpleHistoryText(
          "Office Accounting",
          `${formulaType} formula updated to "${nextFormula}" (result ${toFormulaResultInteger(sumValue)} ${currency})`,
        ),
      );

      setNoteValue("");
      onChange(nextFormula);
      await onRefresh?.();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDoneDisabled =
    isSubmitting || sumValue == null || !formula.trim() || !noteValue.trim();

  return (
    <div ref={popupRef} className={`custom-calculate-popup ${openLeft ? "open-left" : ""}`}>
      <div className="custom-calculate-popup-title">
        <div className="custom-calculate-popup-title-text">
          <p>{title}</p>
        </div>
        <div className="custom-calculate-popup-title-close" onClick={onClose}>
          <CloseSvg />
        </div>
      </div>

      <div className="custom-calculate-popup-input">
        <input
          type="text"
          value={formula}
          onChange={(e) => setFormula(e.target.value)}
          placeholder="=0+0"
          disabled={isSubmitting}
        />
      </div>

      <div className="custom-calculate-popup-sum">
        <p>Sum:</p>
        <p>
          {formattedSum || "0.00"}
          <span> ({currency})</span>
        </p>
      </div>

      <div
        className={`custom-calculate-popup-comments-toggle ${isCommentsOpen ? "open" : ""}`}
        onClick={() => setIsCommentsOpen((prev) => !prev)}
      >
        <p>Comments</p>
        <ArrowSvg />
      </div>

      {isCommentsOpen && displayedComments.length > 0 && (
        <div className="custom-calculate-popup-comments">
          {displayedComments.map((item) => (
            <div className="custom-calculate-popup-comments-item" key={item.id ?? `${item.timestamp}-${item.note}`}>
              <div className="custom-calculate-popup-comments-item-header">
                <div className="custom-calculate-popup-comments-item-meta">
                  <div className="custom-calculate-popup-comments-item-logo">
                    <ProfileSvg />
                  </div>
                  <p>{item.timestamp?.trim() || TEXT_PLACEHOLDER}</p>
                </div>
                <p className="custom-calculate-popup-comments-item-amount">
                  {formatFormulaResultAmount(item.formula_result_new) || TEXT_PLACEHOLDER}
                </p>
              </div>
              <div className="custom-calculate-popup-comments-item-body">
                <p>{item.note?.trim() || TEXT_PLACEHOLDER}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={`custom-calculate-popup-note ${isCommentsOpen && displayedComments.length > 0 ? "compact" : ""}`}>
        <textarea
          placeholder="Note"
          value={noteValue}
          onChange={(e) => setNoteValue(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="custom-calculate-popup-buttons">
        <BtnDefault onClick={handleClear} disabled={isSubmitting}>
          <p>Clear</p>
        </BtnDefault>
        <BtnDefault onClick={handleDone} disabled={isDoneDisabled}>
          <p>Done</p>
        </BtnDefault>
      </div>
    </div>
  );
};

export default CustomCalculatePopUp;
